import type { ReparseEvidence, SourceAnchor } from '@/services/ingestion'

/** How a re-parse proposal names a value and the place in a document it was read. */

export function show(value: unknown, field = ''): string {
  if (value === null || value === undefined || value === '') return 'Not recorded'
  if (typeof value === 'number') {
    if (/(?:amount|price|value|noi|cost|rent|equity)/i.test(field)) {
      return value.toLocaleString(undefined, {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    }
    if (/(?:ltv|ltc|occupancy|cap_rate|confidence)/i.test(field)) {
      return `${(value <= 10 ? value * 100 : value).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })}%`
    }
    if (/dscr/i.test(field)) return `${value.toFixed(2)}x`
    return value.toLocaleString(undefined, { maximumFractionDigits: 6 })
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function label(field: string): string {
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function sourceKind(source?: string | null): SourceAnchor['kind'] | undefined {
  if (!source) return undefined
  if (/\.pdf$/i.test(source)) return 'pdf'
  if (/\.docx?$/i.test(source)) return 'docx'
  if (/\.pptx?$/i.test(source)) return 'pptx'
  if (/\.xlsx?$/i.test(source)) return 'xlsx'
  if (/\.csv$/i.test(source)) return 'csv'
  if (/\.eml$/i.test(source)) return 'email_body'
  if (/\.txt$/i.test(source)) return 'text'
  return undefined
}

export function sourceAnchor(evidence: ReparseEvidence): SourceAnchor | null {
  const merged = { ...(evidence.anchor ?? {}), ...(evidence.locator ?? {}) }
  if (evidence.lineNo != null && merged.lineNo == null) merged.lineNo = evidence.lineNo
  if (evidence.page != null && merged.page == null) merged.page = evidence.page
  if (evidence.cellRef && !merged.cell) merged.cell = evidence.cellRef
  if (evidence.sourceSha256 && !merged.sha256) merged.sha256 = evidence.sourceSha256
  // A line number can be relative to an extracted region. The verbatim value
  // is the most robust anchor and lets the viewer highlight the exact text even
  // when a document was regenerated or OCR inserted lines before it.
  if (evidence.raw && !merged.snippet) merged.snippet = evidence.raw
  if (!merged.kind) merged.kind = sourceKind(evidence.source)
  return Object.keys(merged).length ? merged : null
}
